import React, { useState } from 'react';
import { IndianRupee, FileText, Printer, Loader2, Calendar, CreditCard } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useBillingUtils } from '@/hooks/useBillingUtils';
import { InvoiceStatusBadge } from './InvoiceCard';
// Import centralized types
import type { Invoice } from '@/types';

interface PaymentRecordModalProps {
  isOpen: boolean;
  onClose: () => void;
  invoice: Invoice | null;
  onRecordPayment: (amount: number, method: string) => void;
  loading?: boolean;
}

export function PaymentRecordModal({
  isOpen,
  onClose,
  invoice,
  onRecordPayment,
  loading = false
}: PaymentRecordModalProps) {
  const [paymentAmount, setPaymentAmount] = useState(0);
  const [paymentMethod, setPaymentMethod] = useState('Cash');
  const { formatCurrency } = useBillingUtils();

  // Update payment amount when invoice changes
  React.useEffect(() => {
    if (invoice) {
      setPaymentAmount(invoice.balance_amount);
    }
  }, [invoice]);

  const handleSubmit = () => {
    if (!invoice || paymentAmount <= 0) return;
    onRecordPayment(paymentAmount, paymentMethod);
  };

  const handleClose = () => {
    setPaymentAmount(0);
    setPaymentMethod('Cash');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader className="border-b pb-4">
          <DialogTitle className="flex items-center gap-2">
            <IndianRupee className="w-5 h-5 text-success" />
            Record Payment
          </DialogTitle>
        </DialogHeader>

        {invoice && (
          <div className="space-y-6">
            <div className="medical-section p-4 bg-muted/30 rounded-lg">
              <div className="text-sm space-y-2">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Invoice:</span>
                  <span className="font-medium text-foreground">{invoice.invoice_number}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Patient:</span>
                  <span className="font-medium text-foreground">{invoice.patient_name}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Total Amount:</span>
                  <span className="font-medium text-foreground">{formatCurrency(invoice.total_amount)}</span>
                </div>
                <div className="flex justify-between border-t pt-2">
                  <span className="text-muted-foreground">Balance Due:</span>
                  <span className="font-semibold text-warning">
                    {formatCurrency(invoice.balance_amount)}
                  </span>
                </div>
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Payment Amount *
                </label>
                <div className="relative">
                  <IndianRupee className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground" />
                  <Input
                    type="number"
                    min="0"
                    max={invoice.balance_amount}
                    step="0.01"
                    value={paymentAmount}
                    onChange={(e) => setPaymentAmount(parseFloat(e.target.value) || 0)}
                    placeholder="Enter payment amount"
                    className="pl-10"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Payment Method
                </label>
                <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Cash">Cash</SelectItem>
                    <SelectItem value="UPI">UPI</SelectItem>
                    <SelectItem value="Card">Card</SelectItem>
                    <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                    <SelectItem value="Insurance">Insurance</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button 
            variant="outline" 
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleSubmit}
            disabled={loading || paymentAmount <= 0 || paymentAmount > (invoice?.balance_amount || 0)}
            className="bg-success hover:bg-success/80 text-success-foreground"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Recording...
              </>
            ) : (
              'Record Payment'
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

interface InvoiceViewModalProps {
  isOpen: boolean;
  onClose: () => void;
  invoice: Invoice | null;
  onPrint: (invoice: Invoice) => void;
  onPayment?: (invoice: Invoice) => void;
  showPatientInfo?: boolean;
  variant?: 'patient' | 'clinic';
}

export function InvoiceViewModal({
  isOpen,
  onClose,
  invoice,
  onPrint,
  onPayment,
  showPatientInfo = true,
  variant = 'patient'
}: InvoiceViewModalProps) {
  const { formatCurrency, formatDate } = useBillingUtils();

  const handleSavePDF = async (invoice: Invoice) => {
    // Only available in Electron
    if (!globalThis.electronAPI) {
      return;
    }
    
    try {
      const invoiceDate = new Date(invoice.date);
      const dateStr = invoiceDate.toISOString().split('T')[0];
      const patientNameClean = invoice.patient_name.replace(/[^a-zA-Z0-9]/g, '_');
      const filename = `Invoice_${invoice.invoice_number}_${patientNameClean}_${dateStr}.pdf`;
      
      const result = await globalThis.electronAPI.printToPDF({
        paperSize: 'A4',
        margins: { top: 10, bottom: 10, left: 10, right: 10 },
        save: true,
        filename: filename,
        autoOpen: true // Auto-open the saved PDF
      });
      
      // Success handled silently - user gets UI feedback
    } catch (error) {
      console.error('Failed to save PDF:', error);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader className="border-b pb-4">
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-primary" />
            Invoice Details
          </DialogTitle>
        </DialogHeader>

        {invoice && (
          <div className="space-y-6">
            {/* Medical Clinic Header */}
            <div className="text-center border-b pb-4">
              <h3 className="text-xl font-bold text-foreground">Medical Clinic</h3>
              <p className="text-sm text-muted-foreground">Professional Healthcare Services</p>
              <p className="text-lg font-semibold mt-2 text-primary">
                {invoice.invoice_number}
              </p>
            </div>

            {/* Patient & Date Info */}
            {showPatientInfo && (
              <div className="grid grid-cols-2 gap-6">
                <div className="medical-section p-4 bg-muted/30 rounded-lg">
                  <p className="text-sm font-medium text-muted-foreground mb-1">Patient Information</p>
                  <p className="font-semibold text-foreground">{invoice.patient_name}</p>
                  {invoice.patient_phone && (
                    <p className="text-sm text-muted-foreground">{invoice.patient_phone}</p>
                  )}
                  <p className="text-xs text-muted-foreground mt-1">ID: {invoice.patient_id}</p>
                </div>
                
                <div className="medical-section p-4 bg-muted/30 rounded-lg text-right">
                  <p className="text-sm font-medium text-muted-foreground mb-1">Bill Information</p>
                  <p className="font-semibold text-foreground">{formatDate(invoice.date)}</p>
                  <div className="flex items-center justify-end gap-1 mt-1">
                    <CreditCard className="w-3 h-3 text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">{invoice.payment_method}</span>
                  </div>
                  {invoice.doctor_name && (
                    <p className="text-xs text-muted-foreground mt-1">{invoice.doctor_name}</p>
                  )}
                </div>
              </div>
            )}

            {/* Payment Status */}
            <div className="medical-section p-4 bg-muted/30 rounded-lg">
              <div className="flex items-center justify-between">
                <span className="font-medium text-foreground">Payment Status:</span>
                <InvoiceStatusBadge status={invoice.payment_status} size="md" />
              </div>
            </div>

            {/* Services List */}
            {invoice.items && invoice.items.length > 0 && (
              <div className="medical-section p-4 bg-muted/30 rounded-lg">
                <h4 className="font-medium text-foreground mb-3">Services Provided</h4>
                <div className="space-y-2">
                  {invoice.items.map((item, index) => (
                    <div key={index} className="flex justify-between items-center text-sm py-2 border-b border-border last:border-0">
                      <div>
                        <span className="font-medium">{item.service_name}</span>
                        <span className="text-muted-foreground ml-2">× {item.quantity || 1}</span>
                      </div>
                      <span className="font-medium">{formatCurrency(item.total_price || item.unit_price || 0)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Amount Details */}
            <div className="medical-section p-4 bg-muted/30 rounded-lg">
              <div className="space-y-3">
                <div className="flex justify-between text-lg">
                  <span className="font-semibold text-foreground">Total Amount:</span>
                  <span className="font-bold text-primary">
                    {formatCurrency(invoice.total_amount)}
                  </span>
                </div>
                
                {invoice.paid_amount > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Amount Paid:</span>
                    <span className="font-medium text-success">
                      {formatCurrency(invoice.paid_amount)}
                    </span>
                  </div>
                )}
                
                {invoice.balance_amount > 0 && (
                  <div className="flex justify-between text-sm border-t pt-2">
                    <span className="text-muted-foreground">Balance Due:</span>
                    <span className="font-semibold text-warning">
                      {formatCurrency(invoice.balance_amount)}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          
          <Button 
            variant="outline" 
            className="flex items-center gap-2"
            onClick={() => invoice && onPrint(invoice)}
          >
            <Printer className="w-4 h-4" />
            Print
          </Button>
          
          {/* Save PDF Button - Electron only */}
          {globalThis.electronAPI && invoice && (
            <Button
              variant="outline"
              className="flex items-center gap-2 text-primary border-primary hover:bg-primary/10"
              onClick={() => handleSavePDF(invoice)}
            >
              <FileText className="w-4 h-4" />
              Save PDF
            </Button>
          )}
          
          {/* Payment Button */}
          {invoice && invoice.balance_amount > 0 && onPayment && (
            <Button 
              onClick={() => {
                onClose();
                onPayment(invoice);
              }}
              className="bg-success hover:bg-success/80 text-success-foreground"
            >
              <IndianRupee className="w-4 h-4 mr-2" />
              Record Payment
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
} 